import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { CalendarBlank } from "@phosphor-icons/react";

const TYPE_META = [
  { key: "passport", label: "Passport", color: "#ef4444" },
  { key: "visa", label: "Visa", color: "#f59e0b" },
  { key: "english", label: "IELTS / PTE", color: "#0ea5e9" },
  { key: "naati", label: "NAATI", color: "#d946ef" },
  { key: "skill_assessment", label: "Skill Assessment", color: "#6366f1" },
  { key: "py", label: "PY", color: "#14b8a6" },
  { key: "tuition_fee", label: "Tuition Fee", color: "#22c55e" },
];

const TimelineChart = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/expirations").then((r) => setData(r.data.by_month)); }, []);

  const { months, maxTotal } = useMemo(() => {
    if (!data) return { months: [], maxTotal: 1 };
    const totals = data.map((m) => TYPE_META.reduce((a, t) => a + (m[t.key] || 0), 0));
    return { months: data, maxTotal: Math.max(1, ...totals) };
  }, [data]);

  if (!data) return null;

  return (
    <div
      className="bg-card border border-black/5 dark:border-white/10 rounded-2xl p-6"
      data-testid="dashboard-timeline"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#007AFF]/10 text-[#007AFF] flex items-center justify-center">
            <CalendarBlank size={18} weight="duotone" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Next 12 months</p>
            <h3 className="font-heading font-semibold text-lg">Upcoming expirations timeline</h3>
          </div>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
          {TYPE_META.map((t) => (
            <span key={t.key} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: t.color }} /> {t.label}
            </span>
          ))}
        </div>
      </div>

      {months.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">No upcoming expirations in the next 12 months.</p>
      ) : (
        <div className="pt-2">
          <div className="flex items-end gap-3 h-56" data-testid="timeline-bars">
            {months.map((m) => {
              const total = TYPE_META.reduce((a, t) => a + (m[t.key] || 0), 0);
              return (
                <div key={m.month} className="flex-1 min-w-[24px] h-full flex items-end">
                  <div
                    className="w-full flex flex-col-reverse rounded-t-md overflow-hidden bg-slate-100 dark:bg-white/5"
                    style={{ height: total === 0 ? 4 : `${Math.max(2, (total / maxTotal) * 100)}%` }}
                    title={`${total} expiring`}
                  >
                    {TYPE_META.map((t) => {
                      const count = m[t.key] || 0;
                      if (!count) return null;
                      const h = (count / total) * 100;
                      return (
                        <div key={t.key} style={{ height: `${h}%`, background: t.color }} title={`${t.label}: ${count}`} />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-3 mt-2">
            {months.map((m) => {
              const total = TYPE_META.reduce((a, t) => a + (m[t.key] || 0), 0);
              return (
                <div key={m.month} className="flex-1 min-w-[24px] flex flex-col items-center gap-0.5">
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(`${m.month}-01`).toLocaleDateString(undefined, { month: "short" })}
                  </span>
                  <span className="text-xs font-medium tabular-nums">{total}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimelineChart;
